// File: client/src/components/rules/MonitoringView.js
import React, { useState, useEffect } from 'react';
import {
    Tabs,
    Space,
    Button,
    Select,
    DatePicker,
    Spin
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useSharedData } from '../contexts/SharedDataContext';
import axios from 'axios';
import moment from 'moment';
import { useNodesState, useEdgesState } from 'reactflow';
import 'reactflow/dist/style.css';

// Import subcomponents
import StatsOverview from './monitoring/StatsOverview';
import FlowVisualization from './monitoring/FlowVisualization';
import ExecutionLogs from './monitoring/ExecutionLogs';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { Option } = Select;

const MonitoringView = ({ workflows, currentWorkflowId, onSelectWorkflow }) => {
    // State for monitoring data
    const [activeWorkflowId, setActiveWorkflowId] = useState(currentWorkflowId || '');
    const [dateRange, setDateRange] = useState([moment().subtract(7, 'days'), moment()]);
    const [loading, setLoading] = useState(false);
    const [monitoringData, setMonitoringData] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [executionHistory, setExecutionHistory] = useState([]);
    const [selectedExecution, setSelectedExecution] = useState(null);
    const [executionDetail, setExecutionDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Workflow visualization state
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    // Auth and global data
    const { getAccessTokenSilently } = useAuth0();
    const { selectedTenant } = useSharedData();

    // Load monitoring data when workflow or date range changes
    useEffect(() => {
        if (activeWorkflowId) {
            fetchMonitoringData();
            fetchExecutionHistory();
        }
    }, [activeWorkflowId, dateRange]);

    // Update active workflow when currentWorkflowId changes
    useEffect(() => {
        if (currentWorkflowId && currentWorkflowId !== activeWorkflowId) {
            setActiveWorkflowId(currentWorkflowId);
        } else if (!activeWorkflowId && workflows.length > 0) {
            setActiveWorkflowId(workflows[0].id);
        }
    }, [currentWorkflowId, workflows, activeWorkflowId]);

    // Set up workflow visualization when monitoring data changes
    useEffect(() => {
        if (monitoringData?.workflow) {
            setNodes(monitoringData.workflow.nodes || []);
            setEdges(monitoringData.workflow.edges || []);

            // Update node states with execution counts
            if (monitoringData.nodeCounts) {
                // Update node data with execution counts
                const updatedNodes = (monitoringData.workflow.nodes || []).map(node => {
                    const count = monitoringData.nodeCounts[node.id] || 0;

                    return {
                        ...node,
                        data: {
                            ...node.data,
                            executionCount: count
                        }
                    };
                });

                setNodes(updatedNodes);
            }
        }
    }, [monitoringData]);

    // Fetch monitoring data
    const fetchMonitoringData = async () => {
        if (!activeWorkflowId || !dateRange[0] || !dateRange[1]) return;

        setLoading(true);
        try {
            const token = await getAccessTokenSilently();
            const response = await axios.get(`/api/workflows/${activeWorkflowId}/monitoring`, {
                params: {
                    startDate: dateRange[0].toISOString(),
                    endDate: dateRange[1].toISOString()
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-tenant': selectedTenant
                }
            });

            setMonitoringData(response.data);
        } catch (error) {
            console.error('Error fetching monitoring data:', error);
            setMonitoringData(null);
        } finally {
            setLoading(false);
        }
    };

    // Fetch execution history
    const fetchExecutionHistory = async () => {
        if (!activeWorkflowId || !dateRange[0] || !dateRange[1]) return;

        setLoading(true);
        try {
            const token = await getAccessTokenSilently();
            const response = await axios.get(`/api/workflows/${activeWorkflowId}/executions`, {
                params: {
                    startDate: dateRange[0].toISOString(),
                    endDate: dateRange[1].toISOString(),
                    limit: 100
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-tenant': selectedTenant
                }
            });

            setExecutionHistory(response.data);
        } catch (error) {
            console.error('Error fetching execution history:', error);
            setExecutionHistory([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch execution detail
    const fetchExecutionDetail = async (executionId) => {
        setDetailLoading(true);
        try {
            const token = await getAccessTokenSilently();
            const response = await axios.get(`/api/workflows/executions/${executionId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-tenant': selectedTenant
                }
            });

            setExecutionDetail(response.data);
        } catch (error) {
            console.error('Error fetching execution detail:', error);
            setExecutionDetail(null);
        } finally {
            setDetailLoading(false);
        }
    };

    // Handle workflow selection
    const handleWorkflowChange = (workflowId) => {
        setActiveWorkflowId(workflowId);
        if (onSelectWorkflow) {
            onSelectWorkflow(workflowId);
        }
    };

    // Handle date range change
    const handleDateRangeChange = (dates) => {
        setDateRange(dates);
    };

    // Handle execution selection
    const handleExecutionSelect = (execution) => {
        setSelectedExecution(execution);
        fetchExecutionDetail(execution.id);
    };

    // Handle refresh data
    const handleRefresh = () => {
        fetchMonitoringData();
        fetchExecutionHistory();
    };

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <Space>
                    <Select
                        style={{ width: 300 }}
                        placeholder="Select Workflow"
                        value={activeWorkflowId}
                        onChange={handleWorkflowChange}
                    >
                        {workflows.map(workflow => (
                            <Option key={workflow.id} value={workflow.id}>
                                {workflow.name}
                            </Option>
                        ))}
                    </Select>

                    <RangePicker
                        value={dateRange}
                        onChange={handleDateRangeChange}
                        allowClear={false}
                    />
                </Space>

                <Button
                    icon={<ReloadOutlined />}
                    onClick={handleRefresh}
                >
                    Refresh
                </Button>
            </div>

            <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <TabPane tab="Overview" key="overview">
                    <StatsOverview
                        loading={loading}
                        monitoringData={monitoringData}
                    />
                </TabPane>

                <TabPane tab="Flow Visualization" key="flow">
                    <FlowVisualization
                        loading={loading}
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                    />
                </TabPane>

                <TabPane tab="Execution Logs" key="logs">
                    <ExecutionLogs
                        loading={loading}
                        executionHistory={executionHistory}
                        onRefresh={fetchExecutionHistory}
                        selectedExecution={selectedExecution}
                        onExecutionSelect={handleExecutionSelect}
                        detailLoading={detailLoading}
                        executionDetail={executionDetail}
                    />
                </TabPane>
            </Tabs>
        </div>
    );
};

export default MonitoringView;
